/**
 * Middleware de seguridad para validación de archivos.
 * * Funcionalidad:
 * 1. Validación de tamaño (Max 2MB).
 * 2. Validación de extensión permitida.
 * 3. Inspección profunda de JSON para prevenir DOM XSS (bloquea etiquetas maliciosas).
 * 4. Validación de "Magic Numbers" para asegurar la integridad de imágenes JPG/PNG.
 */

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'json'];
const MAX_SIZE = 2 * 1024 * 1024; // 2MB

// Patrón para detectar vectores de ataque XSS (etiquetas de script, imagen con error, etc.)
// Este patrón detiene el ataque: <img src=x onerror="alert(...)">
const XSS_PAYLOAD_PATTERN = /<(script|img|iframe|svg|body|details)|on\w+\s*=/i;

export const fileSecurity = async (file) => {
  // 1. Validación de tamaño
  if (file.size > MAX_SIZE) {
    throw new Error("El archivo es demasiado grande (Máximo 2MB)");
  }

  // 2. Validación de extensión por nombre
  const ext = file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    throw new Error(`La extensión .${ext} no está permitida`);
  }

  // 3. Validación de contenido para archivos JSON (Protección contra XSS)
  if (ext === 'json') {
    try {
      const text = await file.text();
      
      // BLOQUEO CRÍTICO: Si el texto contiene patrones de ejecución de código, se rechaza
      if (XSS_PAYLOAD_PATTERN.test(text)) {
        throw new Error("Alerta de Seguridad: Se detectó contenido potencialmente malicioso (XSS) en el archivo JSON.");
      }

      JSON.parse(text); // Valida que la estructura sea un JSON válido
      return true;
    } catch (e) {
      throw new Error(e.message.includes("Alerta de Seguridad") 
        ? e.message 
        : "JSON Inválido: El contenido no tiene una estructura JSON correcta.");
    }
  }

  // 4. Validación de Firmas Reales (Magic Numbers) para imágenes
  const buffer = await file.arrayBuffer();
  const uint = new Uint8Array(buffer).subarray(0, 4);
  let header = "";
  for (let i = 0; i < uint.length; i++) {
    // Convertimos a hexadecimal asegurando siempre 2 dígitos (ej: 09 en lugar de 9)
    header += uint[i].toString(16).padStart(2, '0');
  }

  // Firmas conocidas: PNG (89504e47) | JPG (ffd8ff)
  const isPNG = header.startsWith("89504e47");
  const isJPG = header.startsWith("ffd8ff");

  if (ext === 'png' && !isPNG) {
    throw new Error("Firma inválida: El archivo no es un PNG real.");
  }
  
  if ((ext === 'jpg' || ext === 'jpeg') && !isJPG) {
    throw new Error("Firma inválida: El archivo no es un JPG real.");
  }

  if (!isPNG && !isJPG) {
    throw new Error("Contenido no permitido: Solo se admiten imágenes reales o JSON seguro.");
  }

  return true;
};